import { useMemo } from 'react';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  ChannelPostsDocument,
  ChannelStatsDocument,
  CreatePlaylistPostDocument,
  CreateReleasePostDocument,
  gql,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  query ChannelPosts($channelId: String!, $pagination: CursorConnectionArgs!) {
    channelPosts(channelId: $channelId, pagination: $pagination) {
      edges {
        cursor
        node {
          id
          __typename
          createdAt
          webappUri
          commentCount
          creator {
            id
            ...FeedActivityItemUserInfo
          }
          ...PostGroupItem
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

RefetchOnComplete({
  refetch: [ChannelPostsDocument, ChannelStatsDocument],
  trigger: [CreateReleasePostDocument, CreatePlaylistPostDocument],
});

export function useChannelPosts({ channelId, limit }: { channelId: string; limit: number }) {
  const {
    orderedList: posts,
    isLoadingNewPage: loading,
    loadMoreNextPage: loadMore,
    error,
    hasNextPage = false,
    refetch,
  } = useInfiniteQuery(ChannelPostsDocument, {
    getNextPageParam({ data }) {
      return (
        data.channelPosts.pageInfo.hasNextPage && {
          after: data.channelPosts.pageInfo.endCursor,
        }
      );
    },
    filterQueryKey: {
      channelId,
    },
    variables({ pageParam }) {
      const after = pageParam?.after || null;

      return {
        pagination: {
          first: after == null ? limit * 2 : limit,
          after,
        },
        channelId,
      };
    },
    list({ channelPosts }) {
      return (
        channelPosts.edges
          // Filter out posts that are not supported by the Channel Page
          .filter(
            edge =>
              edge.node.__typename === 'PlaylistPost' || edge.node.__typename === 'ReleasePost',
          )
          .map(edge => edge.node)
      );
    },
    uniq({ id }) {
      return id;
    },
    // Don't refetch on window focus or reconnect to avoid breaking scroll restoration  on back button
    // Or losing scroll position when user was scrolled down
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  const isEmpty = useMemo(
    () => posts.length === 0 && !loading && !error,
    [error, loading, posts.length],
  );

  return { posts, loading, loadMore, error, hasNextPage, refetch, isEmpty };
}
